import { useContext, useMemo } from 'react';

import { useMutation } from '@apollo/client';
import { IContextMenuItem } from '@smartshell/ui-kit';
import { truncate } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { FeaturesContext } from 'features';
import { LocalizationContext } from 'localization';
import {
  DELETE_BILLING_SHORTCUT,
  IDeleteShortcutRequest,
  IDeleteShortcutResponse,
  IShortcut,
  IShortcutGroup,
  IUpdateShortcutRequest,
  IUpdateShortcutResponse,
  UPDATE_SHORTCUT,
} from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import notification from 'services/notificationService';
import { ApplicationType, ESyncScope } from 'types';

import s from './ContextMenuItems.module.scss';

export const useContextMenuItems = (
  refetch: VoidFunction,
  shortcut: IShortcut | null,
  groups: IShortcutGroup[],
  appType: ApplicationType,
  defaultGroupTitle: string
): Array<IContextMenuItem<IShortcut>> => {
  const { t } = useContext(LocalizationContext);
  const navigate = useNavigate();
  const { shortcuts_sync_enabled } = useContext(FeaturesContext);

  const [deleteShortcut, { loading: deleteShortcutProcessing }] = useMutation<
    IDeleteShortcutResponse,
    IDeleteShortcutRequest
  >(DELETE_BILLING_SHORTCUT, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const [updateShortcut] = useMutation<
    IUpdateShortcutResponse,
    IUpdateShortcutRequest
  >(UPDATE_SHORTCUT, {
    onCompleted: () => {
      refetch();
    },
    onError: console.error,
  });

  const groupSubMenu: IContextMenuItem<IShortcut>[] = useMemo(() => {
    if (!groups.length || !shortcut) return [];

    return groups.map((group) => ({
      key: String(group.id),
      text:
        group.groupType === 'DEFAULT'
          ? defaultGroupTitle
          : truncate(group.title, { length: 24, separator: '...' }),
      disabled: group.id === shortcut.group_id,
      className: s.menu_item,
      onClick: () => {
        updateShortcut({
          variables: {
            id: shortcut.id,
            input: {
              group_id: group.id,
              sort: 0,
            },
          },
        });
      },
    }));
  }, [groups, shortcut, appType, updateShortcut]);

  const deleteSubMenu = useMemo(() => {
    return [
      {
        key: 'remove',
        text: t('ctx.remove.current'),
        onClick: ({ id, type }: { id: number; type: ApplicationType }) => {
          if (deleteShortcutProcessing) {
            return;
          }
          openConfirmDialog(t, 'remove', {
            title: t('modal.delete.club.network.title_2'),
            message: t('modal.delete.club.network.description_2'),
            acceptTitle: t('delete'),
            declineTitle: t('cancel_2'),
          }).then(({ status }) => {
            if (status === 'ACCEPTED') {
              deleteShortcut({
                variables: { id, sync: ESyncScope.LOCAL },
              }).then(() => {
                notification.info({
                  t,
                  title: t(
                    type === 'GAME'
                      ? 'application.game.deleted'
                      : 'application.app.deleted'
                  ),
                  type: 'success',
                  dismiss: 5,
                });
              });
            }
          });
        },
      },
      {
        key: 'remove2',
        className: s['remove-item'],
        text: t('ctx.remove.all'),
        onClick: ({ id, type }: { id: number; type: ApplicationType }) => {
          if (deleteShortcutProcessing) {
            return;
          }
          openConfirmDialog(t, 'remove', {
            title: t('modal.delete.club.network.title_1'),
            message: t('modal.delete.club.network.description_1'),
            acceptTitle: t('delete'),
            declineTitle: t('cancel_2'),
          }).then(({ status }) => {
            if (status === 'ACCEPTED') {
              deleteShortcut({
                variables: { id, sync: ESyncScope.LOCAL },
              }).then(() => {
                notification.info({
                  t,
                  title: t(
                    type === 'GAME'
                      ? 'application.game.deleted'
                      : 'application.app.deleted'
                  ),
                  type: 'success',
                  dismiss: 5,
                });
              });
            }
          });
        },
      },
    ];
  }, []);

  return useMemo(
    () => [
      {
        key: 'edit',
        text: t('edit'),
        iconName: 'EditIcon',
        onClick: ({ id }) => {
          navigate(`/applications/manage?id=${id}`);
        },
      },
      {
        key: 'changeGroup',
        text:
          appType === 'GAME'
            ? t('application.game.group.title')
            : t('application.app.group.title'),
        iconName: 'FolderIcon',
        disabled: groups.length === 0,
        subMenu: groupSubMenu,
        isLastInGroup: true,
      },
      {
        key: 'delete',
        className: s['remove-item'],
        text: t('delete'),
        iconName: 'BucketIcon',
        onClick: ({ id }) => {
          if (deleteShortcutProcessing) {
            return;
          }

          if (!shortcuts_sync_enabled) {
            deleteShortcut({ variables: { id } }).then(() => {
              notification.info({
                t,
                title: t(
                  appType === 'GAME'
                    ? 'application.game.deleted'
                    : 'application.app.deleted'
                ),
                type: 'success',
                dismiss: 5,
              });
            });
          }
        },
        subMenu: shortcuts_sync_enabled ? deleteSubMenu : undefined,
      },
    ],
    [appType, groups, shortcut, groupSubMenu]
  );
};
